import { useTranslation } from 'react-i18next';

export type IPageLoadingSpinnerProps = {
  name: string;
};

export function PageLoadingSpinner({ name }: IPageLoadingSpinnerProps) {
  const { t } = useTranslation('general');
  return (
    <div
      data-testid="page-loading-spinner"
      className="flex flex-col gap-4 w-full justify-center items-center py-8"
    >
      <MoonSpinner className="text-primary" size={50} />
      <p className="text-center text-base text-neut-prim">
        {t('general.loading')} {name}
      </p>
    </div>
  );
}

export type MoonSpinnerProps = {
  size?: number;
  className?: string;
};

/**
 * Builds a filled SVG path for a tapered arc.
 * The arc is thick at its midpoint and tapers to a sharp point at both ends.
 */
function buildTaperedArcPath(
  cx: number,
  cy: number,
  outerR: number,
  maxThickness: number,
  startAngle: number,
  arcSpan: number,
  segments = 64
): string {
  const outer: [number, number][] = [];
  const inner: [number, number][] = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + t * arcSpan;
    // Sine taper: 0 at both tips, 1 at midpoint
    const taper = Math.sin(t * Math.PI);
    const thickness = maxThickness * taper;

    outer.push([cx + outerR * Math.cos(angle), cy + outerR * Math.sin(angle)]);
    inner.push([
      cx + (outerR - thickness) * Math.cos(angle),
      cy + (outerR - thickness) * Math.sin(angle),
    ]);
  }

  const fmt = (n: number) => n.toFixed(2);

  const d = [
    `M ${fmt(outer[0][0])} ${fmt(outer[0][1])}`,
    ...outer.slice(1).map(([x, y]) => `L ${fmt(x)} ${fmt(y)}`),
    ...[...inner].reverse().map(([x, y]) => `L ${fmt(x)} ${fmt(y)}`),
    'Z',
  ].join(' ');

  return d;
}

function MoonSpinner({ size = 28, className }: MoonSpinnerProps) {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.44;
  const maxThickness = size * 0.045;
  const startAngle = -Math.PI / 2; // start at top
  const arcSpan = Math.PI; // 180°

  const d = buildTaperedArcPath(
    cx,
    cy,
    outerR,
    maxThickness,
    startAngle,
    arcSpan
  );

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-label="Loading"
      className={className}
    >
      <path d={d} fill="currentColor">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${cx} ${cy}`}
          to={`360 ${cx} ${cy}`}
          dur="0.75s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}
